/**
 * @fileoverview Application Logger Utility
 * @description Environment-aware logging utility that only outputs to console in development.
 * In production, detailed errors are suppressed to prevent information leakage.
 * 
 * SECURITY: This prevents exposing database schema, query patterns, or
 * operational details in browser console for production builds.
 */

const isDevelopment = import.meta.env.DEV;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string;
  timestamp: string;
  data?: unknown;
}

/**
 * Formats a log entry for console output
 */
function formatLogEntry(entry: LogEntry): string {
  const prefix = entry.context ? `[${entry.context}]` : '';
  return `${entry.timestamp} ${entry.level.toUpperCase()} ${prefix} ${entry.message}`;
}

/**
 * Creates a structured log entry
 */
function createLogEntry(
  level: LogLevel,
  message: string,
  context?: string,
  data?: unknown
): LogEntry {
  return {
    level,
    message,
    context,
    timestamp: new Date().toISOString(),
    data,
  };
}

/**
 * Application Logger
 * 
 * In development: Logs all messages to console with full details
 * In production: Suppresses all console output to prevent information leakage
 * 
 * @example
 * ```ts
 * import { logger } from '@/lib/logger';
 * 
 * logger.error('Failed to fetch data', 'UserService', error);
 * logger.info('User logged in', 'Auth');
 * logger.debug('Query params', 'API', { userId, page });
 * ```
 */
export const logger = {
  /**
   * Log debug information (development only)
   */
  debug: (message: string, context?: string, data?: unknown): void => {
    if (isDevelopment) {
      const entry = createLogEntry('debug', message, context, data);
      console.debug(formatLogEntry(entry), data ?? '');
    }
  },

  /**
   * Log informational messages (development only)
   */
  info: (message: string, context?: string, data?: unknown): void => {
    if (isDevelopment) {
      const entry = createLogEntry('info', message, context, data);
      console.info(formatLogEntry(entry), data ?? '');
    }
  },

  /**
   * Log warnings (development only)
   */
  warn: (message: string, context?: string, data?: unknown): void => {
    if (isDevelopment) {
      const entry = createLogEntry('warn', message, context, data);
      console.warn(formatLogEntry(entry), data ?? '');
    }
  },

  /**
   * Log errors (development only)
   * In production, errors are silently captured without console output
   */
  error: (message: string, context?: string, error?: unknown): void => {
    if (isDevelopment) {
      const entry = createLogEntry('error', message, context, error);
      console.error(formatLogEntry(entry), error ?? '');
    }
    // In production, you could send to an error tracking service here:
    // e.g., Sentry.captureException(error);
  },
};

/**
 * Returns a generic user-friendly error message
 * Use this for displaying errors in the UI instead of exposing technical details
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  // In development, we might want more details
  if (isDevelopment && error instanceof Error) {
    return error.message;
  }
  
  // In production, return generic messages
  return 'An unexpected error occurred. Please try again.';
}

export default logger;
